import { makeCygnusApiCall } from "../../../../api/api";
import { FormConfig, SelectedField } from "./IntakeFormAIModal.types";
import { deduplicateFieldsById } from "./IntakeFormAIModal.utils";
import { fetchFirmTemplateFieldDetails } from "../../../engagement_letter/CreateOrEditEditorComponent/CreateOrEditEditorComponent.service";

export const fetchIntakeFormFields = async (): Promise<SelectedField[]> => {
  const data = await fetchFirmTemplateFieldDetails();
  const selected: SelectedField[] = data?.SelectedFields ?? [];
  return deduplicateFieldsById(selected);
};

export const fetchDefaultIntakeFields = async (): Promise<string[] | null> => {
  const firm_id = localStorage.getItem("firmId");
  const userId = localStorage.getItem("userId");
  if (!firm_id) return null;

  const parameter_name = `intake_form_default_fields.${userId ?? ""}`;

  const res = await makeCygnusApiCall({
    type: "POST",
    url: `/personalization/get_params/`,
    payload: { firm_id, parameter_name },
    show_message: false,
    customized_success_message: "",
    customized_failure_message: "",
    customized_error_message: "",
    isblob: false,
    forMDM: false,
  });

  if (!res?.success) return null;

  const raw = res?.data?.[0]?.params?.[0]?.parameter_value_1 ?? null;
  if (!raw) return null;
  try { return JSON.parse(raw) as string[]; } catch { return null; }
};

export const saveDefaultIntakeFields = async (fieldCodes: string[]): Promise<boolean> => {
  const firm_id = localStorage.getItem("firmId");
  const userId = localStorage.getItem("userId");
  if (!firm_id) return false;

  const parameter_name = `intake_form_default_fields.${userId ?? ""}`;
  const parameter_value_1 = JSON.stringify(fieldCodes);

  const res = await makeCygnusApiCall({
    type: "POST",
    url: `/personalization/set_params/`,
    payload: { firm_id, parameter_name, parameter_value_1 },
    show_message: true,
    customized_success_message: "Default fields saved successfully.",
    customized_failure_message: "Failed to save default fields.",
    customized_error_message: "An error occurred while saving default fields.",
    isblob: false,
    forMDM: false,
  });

  return !!res?.success;
};

export const generateIntakeForm = async (
  config: FormConfig,
  selectedFields: SelectedField[],
  attachment?: File | null
): Promise<void> => {
  let payload: FormData | object;
  let isFile = false;

  if (attachment) {
    const formData = new FormData();
    formData.append("FormName", config.formName);
    formData.append("LineOfService", config.lineOfService?.code ?? "");
    formData.append("FormType", config.formType?.value ?? "");
    formData.append("Fields", JSON.stringify(selectedFields.map((f) => f.code)));
    if (config.instructions?.trim()) {
      formData.append("Instructions", config.instructions.trim());
    }
    formData.append("Attachment", attachment, attachment.name);
    payload = formData;
    isFile = true;
  } else {
    payload = {
      FormName: config.formName,
      LineOfService: config.lineOfService?.code ?? null,
      FormType: config.formType?.value ?? null,
      Fields: selectedFields.map((f) => f.code),
      Instructions: config.instructions?.trim() || null,
    };
  }

  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/forms/form_builder/",
    payload,
    payload_is_file: isFile,
    show_message: true,
    customized_success_message: "Intake form generation has been initiated. This may take a few minutes.",
    customized_failure_message: "Failed to initiate intake form generation.",
    customized_error_message: "An error occurred while initiating intake form generation.",
    forMDM: true,
  });

  if (!response?.success) {
    throw new Error("Failed to generate intake form.");
  }
};

export const modifyForm = async (
  formId: number,
  templateId: number,
  selectedFields: SelectedField[],
  instructions: string,
  formType: string
): Promise<void> => {
  const response = await makeCygnusApiCall({
    type: "PUT",
    url: "/forms/form_builder/",
    payload: {
      FormId: formId,
      TemplateId: templateId,
      Fields: selectedFields.map((f) => f.code),
      Instructions: instructions.trim() || null,
      FormType: formType,
    },
    show_message: true,
    customized_success_message: "Form modification has been initiated. This may take a few minutes.",
    customized_failure_message: "Failed to initiate form modification.",
    customized_error_message: "An error occurred while initiating form modification.",
    forMDM: true,
  });
  if (!response?.success) throw new Error("Failed to modify form.");
};
